// Phase 5.1 — Purchase-history panel, shared by Customer and Vendor.
//
// Replaces the antd <Drawer> + MUI <TreeView> in customer.tsx with a shadcn
// <Sheet> + Accordion (grouped by purchase date). Read-only view — the history
// data comes from the screen (getCustomerHistoryAction / getVendorPurchaseHistoryAction
// result). Empty → EmptyState.
//
// Entity-agnostic: the caller supplies title/subtitle, how to pull an
// invoice-dedup key + amount off each item (customer rows use
// invoiceTotalAmount + product/color/size; vendor rows use totalAmount and
// have no line items), and how to render a single item row.
import * as React from "react";
import moment from "moment";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "ui/sheet";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "ui/accordion";
import { EmptyState } from "../../shared/empty-state";
import AppConstant from "../../Globals/AppConstants";
import { cn } from "lib/utils";

export interface PurchaseHistorySheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  /** Resets the selected financial year whenever the viewed entity changes. */
  resetKey?: any;
  /** How many financial years (current + preceding) to show as buttons. Defaults to 2. */
  yearCount?: number;
  historyByDate: any;
  getItemInvoiceId: (item: any) => string;
  getItemAmount: (item: any) => number;
  renderItem: (item: any, idx: number) => React.ReactNode;
}

// Indian financial year (Apr 1 – Mar 31), labeled start-year–end-year. Built
// from today's date so "current FY" always tracks the real calendar: the
// current FY plus the `yearCount - 1` preceding ones.
function buildFinancialYears(yearCount: number) {
  const today = moment();
  const currentFYStartYear = today.month() >= 3 ? today.year() : today.year() - 1; // April = month index 3
  return Array.from({ length: yearCount }, (_, i) => -i).map((offset) => {
    const startYear = currentFYStartYear + offset;
    return {
      label: `${startYear}–${startYear + 1}`,
      start: `${startYear}-04-01`,
      end: `${startYear + 1}-03-31`,
      isCurrent: offset === 0,
    };
  });
}

// One invoice counted once, summed by the caller-supplied amount — not a
// count/sum of individual line items, which would double-count multi-item
// bills and ignore GST/discount/round-off baked into the invoice total.
function summarize(items: any[], getInvoiceId: (item: any) => string, getAmount: (item: any) => number) {
  const invoiceTotals = new Map<string, number>();
  (items || []).forEach((item: any) => {
    const key = getInvoiceId(item);
    if (key && !invoiceTotals.has(key)) {
      invoiceTotals.set(key, getAmount(item) || 0);
    }
  });
  const value = Array.from(invoiceTotals.values()).reduce((sum, v) => sum + v, 0);
  return { count: invoiceTotals.size, value };
}

export function PurchaseHistorySheet({
  open,
  onClose,
  title,
  subtitle,
  resetKey,
  yearCount = 2,
  historyByDate,
  getItemInvoiceId,
  getItemAmount,
  renderItem,
}: PurchaseHistorySheetProps) {
  const financialYears = React.useMemo(() => buildFinancialYears(yearCount), [yearCount]);
  const currentFY = financialYears.find((fy) => fy.isCurrent) || financialYears[0];
  const [selectedFY, setSelectedFY] = React.useState(currentFY);

  // Reset to the current FY every time a different entity's history is opened.
  React.useEffect(() => {
    if (open) setSelectedFY(currentFY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resetKey]);

  const invoiceEntries = React.useMemo(() => {
    if (!historyByDate) return [];
    return Object.entries(historyByDate as Record<string, any[]>)
      .map(([invId, items]) => ({
        invId,
        date: items?.[0]?.createdOn as string,
        items,
      }))
      .filter((e) => e.date && moment(e.date).isBetween(selectedFY.start, selectedFY.end, undefined, "[]"));
  }, [historyByDate, selectedFY]);
  const hasHistory = invoiceEntries.length !== 0;

  const { count: totalCount, value: totalValue } = React.useMemo(
    () => summarize(invoiceEntries.flatMap((e) => e.items), getItemInvoiceId, getItemAmount),
    [invoiceEntries, getItemInvoiceId, getItemAmount]
  );

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle>{AppConstant?.purchaseHistory}</SheetTitle>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
          <div className="space-y-0.5">
            <div className="font-medium text-foreground">{title}</div>
            {subtitle && <div className="text-sm text-muted-foreground">{subtitle}</div>}
          </div>

          <div className={cn("grid gap-2", yearCount > 2 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2")}>
            {financialYears.map((fy) => (
              <button
                key={fy.label}
                type="button"
                onClick={() => setSelectedFY(fy)}
                className={cn(
                  "rounded-lg border px-3 py-2 text-center text-sm font-medium transition-colors",
                  fy.label === selectedFY.label
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                {fy.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl border p-4">
              <div className="text-xs text-muted-foreground">Total Purchase Count</div>
              <div className="mt-1 text-xl font-semibold tabular-nums text-foreground">{totalCount}</div>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-xs text-muted-foreground">Total Purchase Value</div>
              <div className="mt-1 text-xl font-semibold tabular-nums text-foreground">₹ {totalValue.toFixed(2)}</div>
            </div>
          </div>

          {hasHistory ? (
            <Accordion key={selectedFY.label} type="multiple" defaultValue={[]}>
              {invoiceEntries.map(({ invId, date, items }) => {
                const invSummary = summarize(items, getItemInvoiceId, getItemAmount);
                return (
                  <AccordionItem key={invId} value={invId}>
                    <AccordionTrigger>
                      <div className="flex flex-1 items-center justify-between pr-2">
                        <span>{moment(date).format("DD,MMM - YYYY")}</span>
                        <span className="text-xs font-normal text-muted-foreground">
                          ₹{invSummary.value.toFixed(2)}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        {items.map((item: any, idx: number) => renderItem(item, idx))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          ) : (
            <EmptyState />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default PurchaseHistorySheet;
